import { Container, Spacer, SpacerVariant, spacerOptions } from "../components/layout"
import { Repeater, types, useAdminContext } from "react-bricks/frontend"
import { useAPIState, useSDK } from "../../shared/context/client"

import Divider from "../decorative/Divider"
import { LazyImage } from "../../shared/components/universal/image"
import React from "react"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useUrlSearchParams } from "../../shared/hooks/use-url-search-params"

interface SubscriptionTeaserProps {
    divider: boolean
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
}

const SubscriptionTeaser: types.Brick<SubscriptionTeaserProps> = ({
    divider,
    spacerTop,
    spacerBottom,
    variantTop,
    variantBottom,
}) => {
    const { t } = useTranslation("translations")
    const client = useSDK()
    const customer_id = useAPIState(s => s.customer_id)
    const [params] = useUrlSearchParams()

    const { data: subscriptionsData } = useQuery({
        queryKey: ["getSubscriptions", { customer_id: customer_id }],
        queryFn: ({ signal }) => client.getSubscriptions(customer_id as string, undefined, signal),
        enabled: !!(customer_id && process.env.EXPONEA_SUBSCRIPTION_CREDIT_EXPRESSION),
        meta: { background: true },
    })

    const { data: expressionData } = useQuery({
        queryKey: [
            "getExpression",
            { customer_id: customer_id, expression_id: process.env.EXPONEA_SUBSCRIPTION_CREDIT_EXPRESSION },
        ],
        queryFn: ({ signal }) =>
            client.getCustomerExpression(
                customer_id as string,
                process.env.EXPONEA_SUBSCRIPTION_CREDIT_EXPRESSION as string,
                signal
            ),
        enabled: !!(customer_id && process.env.EXPONEA_SUBSCRIPTION_CREDIT_EXPRESSION),
        meta: { background: true },
    })

    const { isAdmin } = useAdminContext()

    if (
        (!subscriptionsData || subscriptionsData.length > 0 || !expressionData || expressionData.value === 0) &&
        // @ts-ignore
        !params?.slba &&
        !isAdmin
    )
        return null

    return (
        <>
            <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
            <Container>
                <div className="flex flex-col items-center justify-center sm:flex-row">
                    <div className="flex w-full flex-col items-center justify-center sm:w-auto sm:flex-row">
                        <LazyImage
                            url="assets"
                            path={`/products/SLP500/steps/V4/DE/step-${
                                // @ts-ignore
                                expressionData?.value || params.slba || 0
                            }-DE_2x1.png`}
                            srcSet={[{ w: 400, default: true }, { w: 800 }]}
                            sizes="(min-width: 1560px) 400px, (min-width: 1024px) 380px, 300px"
                            quality={100}
                            className="hidden w-[300px] self-start sm:block md:w-[380px] xl:w-[400px]"
                        />
                        <div className="w-full sm:ml-5 sm:w-auto md:ml-10 xl:ml-16">
                            <div className="mb-7 max-w-full text-center sm:max-w-[500px] sm:text-left md:mb-10 lg:mb-16 lg:max-w-[600px]">
                                <h4 className="mb-5 text-xl font-semibold md:mb-7 md:text-3xl/3xl lg:text-5xl/5xl">
                                    {t("subscription_teaser_headline", { lng: "de" })}
                                </h4>
                                <p className="whitespace-pre-wrap text-md md:text-lg lg:text-xl">
                                    {t("subscription_teaser_text_1", { lng: "de" })}{" "}
                                    <strong className="font-semibold">
                                        {t("subscription_teaser_text_strong", {
                                            // @ts-ignore
                                            count: expressionData?.value || params.slba || 0,
                                            lng: "de",
                                        })}
                                    </strong>{" "}
                                    {t("subscription_teaser_text_2", { lng: "de" })}
                                </p>
                            </div>
                            <LazyImage
                                url="assets"
                                path={`/products/SLP500/steps/V4/DE/step-${
                                    // @ts-ignore
                                    expressionData?.value || params.slba || 0
                                }-DE_2x1.png`}
                                srcSet={[{ w: 300, default: true }, { w: 600 }]}
                                sizes="300px"
                                quality={100}
                                className="mx-auto mb-10 w-full max-w-[300px] px-[5px] sm:hidden"
                            />
                            <Repeater
                                propName="repeater"
                                renderWrapper={children => (
                                    <div className="sm:flex">
                                        <div className="space-y-3.5 child:sm:block child:child:sm:w-full child:sm:w-full md:flex md:space-x-3.5 md:space-y-0 child:md:inline-block child:child:md:w-auto child:md:w-auto lg:space-x-5">
                                            {children}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                </div>
            </Container>
            {divider && <Divider spacerTop spacerBottom={false} />}
            <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
        </>
    )
}

SubscriptionTeaser.schema = {
    name: "SubscriptionTeaser",
    label: "Subscription Upgrade",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/SubscriptionTeaser.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "Button",
                    label: "Button",
                    max: 2,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "divider",
            label: "Divider",
            type: types.SideEditPropType.Boolean,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): SubscriptionTeaserProps => ({
        divider: false,
        spacerTop: true,
        spacerBottom: true,
        variantTop: SpacerVariant.Base,
        variantBottom: SpacerVariant.Base,
    }),
}

export default SubscriptionTeaser
